import {
  FormulaCell,
  FormulaExpression,
  FormulaHistoryItem,
  IFormulaExpressionEvaluator,
} from '@dpr-details/models';
import {
  FormulaBlankOperand,
  FormulaDisplayPrecision,
  FormulaDivisionByZeroError,
  getCalendarDay,
  isDivisionByZeroError,
  parseFormulaColumnIds,
} from '@dpr-details/utils';
import { toDecimalFormat } from '@grid/utils/number';

/**
 * Base engine for evaluating formula-type column expressions, one value per
 * row. Status Tracker and Table of Columns implement the abstract hooks so that
 * each matches a row by the identity its own data carries — a row id for one,
 * the anchor column's value for the other.
 *
 * The invariants to know before changing anything here:
 *  - Parameters are read straight from the `$NNN` column ids in the expression.
 *    A saved formula carries no parameter rows, so the expression is the only
 *    reference source.
 *  - History is the previous DPR as it was worked out: built from the
 *    `previousValues` the API sends, or by the offline walk. It is still cut
 *    off inside the engine at the calculated card's date, so a carry-forward
 *    value can only come from an earlier card.
 *  - A self-reference resolves to the same cell's value on that previous DPR,
 *    which is how a running total carries forward. It is detected after
 *    nested formulas are inlined, so an indirect one (root → A → root) resolves
 *    as well, and a nested formula that self-references resolves its own
 *    column's history before being inlined.
 *  - Cells are matched by row id rather than by position, because rows can be
 *    reordered, and always through `String(...)`: a row created offline carries
 *    a uuid until it reaches the API, so a card and its history can hold the
 *    same row as `'5'` and `5`.
 *  - Column id substitution is boundary-safe (`$12` does not clobber `$123`)
 *    and every substituted value is parenthesised, so a negative value stays
 *    well-formed.
 *  - A blank input is substituted as `FormulaBlankOperand`, not as 0, so a
 *    blank divisor gives a quotient of 0 while a divisor typed as 0 shows the
 *    division-by-zero error.
 */
export abstract class FormulaExpressionCalculationService<
  THistory extends FormulaHistoryItem
> {
  protected constructor(
    protected readonly evaluator: IFormulaExpressionEvaluator
  ) {}

  /**
   * Pure calculation for one formula column: per-row `(rowId, cellValue)`
   * pairs, or null when the column is not a formula column.
   *
   * `dprDate` is the date of the card being calculated. History is cut off at
   * it here, so a carry-forward value can only come from an earlier card,
   * whatever a caller hands over. Each value is rounded to `precision` decimals.
   */
  calculateFormula(
    columnId: number,
    history: THistory[],
    dprDate: Date | string | null,
    precision = FormulaDisplayPrecision
  ): { rowId: number | string; cellValue: string | null }[] | null {
    const formula = this.getFormula(columnId);
    if (formula == null) {
      return null;
    }

    try {
      const allParamIds: number[] = [];
      this.collectTransitiveParamIds(
        formula.expression,
        columnId,
        new Set<number>([columnId]),
        allParamIds
      );

      const paramValues = new Map<number, FormulaCell[]>();
      for (const paramColId of allParamIds) {
        if (!paramValues.has(paramColId)) {
          paramValues.set(paramColId, [...this.getDetailItems(paramColId)]);
        }
      }

      return this.calculate(
        formula,
        paramValues,
        FormulaExpressionCalculationService.cardsBefore(history, dprDate),
        precision
      );
    } catch {
      return null;
    }
  }

  private calculate(
    formula: FormulaExpression,
    paramValues: Map<number, FormulaCell[]>,
    history: THistory[],
    precision: number
  ): { rowId: number | string; cellValue: string | null }[] {
    const result: { rowId: number | string; cellValue: string | null }[] = [];
    const selfColumnId = formula.columnId;

    const deferredNestedSelfRefs = new Map<number, string>();
    const baseExpr = this.inlineNestedFormulas(
      formula.expression,
      new Set<number>([selfColumnId]),
      deferredNestedSelfRefs
    );

    for (const rowId of this.collectRowIds(paramValues)) {
      const { expr, hasAnyValue, hasUnresolvedNestedSelfRef } =
        this.buildRowExpression(
          baseExpr,
          rowId,
          selfColumnId,
          deferredNestedSelfRefs,
          paramValues,
          history
        );

      // A row with no contributing value anywhere keeps an empty cell.
      if (!hasAnyValue) {
        continue;
      }

      result.push({
        rowId,
        cellValue: hasUnresolvedNestedSelfRef
          ? null
          : this.evaluateCell(expr, precision),
      });
    }

    return result;
  }

  /**
   * The rows to produce a value for: the grid's own rows, plus any row that
   * only appears in a dependency column, deduplicated. A sparse feature (Status
   * Tracker stores a cell only where a value was entered) has no single column
   * whose cell list covers every row the formula spans, so the union is needed.
   */
  private collectRowIds(
    paramValues: Map<number, FormulaCell[]>
  ): (number | string)[] {
    const rowIds: (number | string)[] = [];
    const seenRowIds = new Set<string>();

    const addRowId = (rowId: number | string): void => {
      const key = String(rowId);
      if (!seenRowIds.has(key)) {
        seenRowIds.add(key);
        rowIds.push(rowId);
      }
    };

    // The feature's own rows first, so the results come back in display order.
    this.getAllRowIds().forEach(addRowId);

    for (const cells of paramValues.values()) {
      cells.forEach((cell) => addRowId(cell.rowId));
    }

    return rowIds;
  }

  /**
   * Recursively inlines the formula columns referenced by `expression`.
   * A non-self-referencing nested formula is inlined globally, since it is
   * identical for every row; a self-referencing one is collected into
   * `deferredNestedSelfRefs` so the per-row loop can resolve that row's own
   * history value before inlining it. Recursing into a deferred formula matters
   * for the same reason: an unresolved reference inside one would fall through
   * to parameter substitution and silently read as a stored value.
   *
   * `inProgress` holds the current path — the owning column plus every
   * enclosing formula — and is scoped to that path rather than global, so a
   * formula referenced from two branches is inlined into both.
   */
  private inlineNestedFormulas(
    expression: string,
    inProgress: Set<number>,
    deferredNestedSelfRefs: Map<number, string>
  ): string {
    let expr = expression;

    for (const refColId of parseFormulaColumnIds(expression)) {
      // The column's own self-reference, or a cycle back to an enclosing
      // formula: the token is left for the per-row pass to resolve from history.
      if (inProgress.has(refColId)) {
        continue;
      }

      const nestedFormula = this.getFormula(refColId);
      if (nestedFormula == null) {
        continue;
      }

      inProgress.add(refColId);
      const nestedInlined = this.inlineNestedFormulas(
        nestedFormula.expression,
        inProgress,
        deferredNestedSelfRefs
      );
      inProgress.delete(refColId);

      // Tested on the inlined form, not the raw one, for the same reason the root
      // self-reference is: a cycle that comes back round to this column (A → B → A)
      // only shows up once B has been inlined.
      if (
        FormulaExpressionCalculationService.referencesColumn(
          nestedInlined,
          refColId
        )
      ) {
        deferredNestedSelfRefs.set(refColId, nestedInlined);
      } else {
        expr = FormulaExpressionCalculationService.replaceColumnId(
          expr,
          refColId,
          `(${nestedInlined})`
        );
      }
    }

    return expr;
  }

  /**
   * Builds the fully-substituted expression for one row: the deferred nested
   * self-refs and the root self-reference resolve from this row's history,
   * then the parameter cells matched by rowId are substituted.
   *
   * Also reports whether the row held any contributing value — one with none
   * is skipped, so an all-empty row stays empty rather than reading "0" — and
   * whether a nested self-reference was left unresolved, which fails the cell
   * instead of letting it read as a number.
   */
  private buildRowExpression(
    baseExpr: string,
    rowId: number | string,
    selfColumnId: number,
    deferredNestedSelfRefs: Map<number, string>,
    paramValues: Map<number, FormulaCell[]>,
    history: THistory[]
  ): {
    expr: string;
    hasAnyValue: boolean;
    hasUnresolvedNestedSelfRef: boolean;
  } {
    let currentExpr = baseExpr;
    let hasAnyValue = false;

    // Resolved on first use (TOC only — null for Status Tracker). Only a
    // self-reference needs the anchor, and answering costs a scan of the
    // card's cells for every row of the column.
    let anchorOptionId: number | null = null;
    let anchorOptionResolved = false;
    const resolveAnchorOptionId = (): number | null => {
      if (!anchorOptionResolved) {
        anchorOptionId = this.getAnchorOptionId(rowId);
        anchorOptionResolved = true;
      }

      return anchorOptionId;
    };

    // Each deferred nested self-reference resolves its own token from history
    // first, and the results are then inlined repeatedly: one deferred formula
    // may reference another, and collection order does not put the referencing
    // formula first. The passes are bounded by the deferred count, so a mutual
    // reference terminates.
    const resolvedNestedSelfRefs = new Map<number, string>();
    for (const [nestedColId, nestedExpr] of deferredNestedSelfRefs) {
      const nestedPrev = this.resolvePreviousValue(
        history,
        nestedColId,
        rowId,
        resolveAnchorOptionId()
      );

      resolvedNestedSelfRefs.set(
        nestedColId,
        FormulaExpressionCalculationService.replaceColumnId(
          nestedExpr,
          nestedColId,
          `(${nestedPrev ?? FormulaBlankOperand})`
        )
      );

      // Counts as the root self-reference's does, so a formula reading a running
      // total shows it on a row with nothing typed today.
      if (nestedPrev !== null) {
        hasAnyValue = true;
      }
    }

    for (let pass = 0; pass < resolvedNestedSelfRefs.size; pass++) {
      const exprBeforePass = currentExpr;

      for (const [nestedColId, nestedResolved] of resolvedNestedSelfRefs) {
        currentExpr = FormulaExpressionCalculationService.replaceColumnId(
          currentExpr,
          nestedColId,
          `(${nestedResolved})`
        );
      }

      if (currentExpr === exprBeforePass) {
        break;
      }
    }

    // A deferred token that outlives the passes would otherwise reach the
    // parameter substitution below and take a stored cell value or "0", so a
    // reference that never resolved would read as an ordinary number.
    const hasUnresolvedNestedSelfRef = [...deferredNestedSelfRefs.keys()].some(
      (nestedColId) =>
        FormulaExpressionCalculationService.referencesColumn(
          currentExpr,
          nestedColId
        )
    );

    // The root self-reference is looked for in the expression built above
    // rather than in the raw formula, so an indirect one (root → A → root)
    // also resolves from history instead of surviving into the evaluator and
    // failing the cell.
    if (
      FormulaExpressionCalculationService.referencesColumn(
        currentExpr,
        selfColumnId
      )
    ) {
      const selfPrev = this.resolvePreviousValue(
        history,
        selfColumnId,
        rowId,
        resolveAnchorOptionId()
      );

      currentExpr = FormulaExpressionCalculationService.replaceColumnId(
        currentExpr,
        selfColumnId,
        `(${selfPrev ?? FormulaBlankOperand})`
      );

      // A recorded previous value counts even when it is 0: a running total that
      // stands at 0 still shows 0, while a row that never held a value stays empty.
      if (selfPrev !== null) {
        hasAnyValue = true;
      }
    }

    // Match the cell by rowId (rows can be reorganised). A missing cell means
    // no stored value, which is blank.
    for (const [paramColId, cells] of paramValues) {
      let value = FormulaExpressionCalculationService.asText(
        cells.find((c) => String(c.rowId) === String(rowId))?.value
      );

      if (!String.isNullOrWhiteSpace(value)) {
        hasAnyValue = true;
      } else {
        value = FormulaBlankOperand;
      }

      currentExpr = FormulaExpressionCalculationService.replaceColumnId(
        currentExpr,
        paramColId,
        `(${value})`
      );
    }

    return { expr: currentExpr, hasAnyValue, hasUnresolvedNestedSelfRef };
  }

  /**
   * A binary double cannot hold 0.1 exactly, so rounding each result keeps a value
   * such as 0.30000000000000004 off the screen. These values are never saved: online,
   * the DPR after this one starts from the API's results, kept to 10 decimals, and
   * offline from values calculated to the same.
   */
  private evaluateCell(currentExpr: string, precision: number): string | null {
    try {
      const value = this.evaluator.evaluate(currentExpr);

      return Number.isNaN(value)
        ? FormulaDivisionByZeroError
        : toDecimalFormat(value, precision);
    } catch {
      return null;
    }
  }

  private collectTransitiveParamIds(
    expression: string,
    selfColId: number,
    visited: Set<number>,
    result: number[]
  ): void {
    for (const paramColId of parseFormulaColumnIds(expression)) {
      // Skip the owning column, and anything already collected, so a cycle
      // terminates.
      if (paramColId === selfColId || visited.has(paramColId)) {
        continue;
      }
      visited.add(paramColId);
      result.push(paramColId);

      const nested = this.getFormula(paramColId);
      if (nested != null) {
        this.collectTransitiveParamIds(
          nested.expression,
          paramColId,
          visited,
          result
        );
      }
    }
  }

  /**
   * A "$" followed by exactly this column's id. The lookahead stops "$12"
   * matching inside "$123" or inside a malformed token like "$12abc", which is
   * left alone rather than half-rewritten. Every helper that reads or rewrites
   * a reference shares it, so "references this column" and "would be
   * substituted" cannot disagree.
   */
  private static columnIdPattern(columnId: number): string {
    return `\\$${columnId}(?!\\w)`;
  }

  private static referencesColumn(
    expression: string,
    columnId: number
  ): boolean {
    return new RegExp(
      FormulaExpressionCalculationService.columnIdPattern(columnId)
    ).test(expression ?? '');
  }

  /**
   * The cards a carry-forward value may come from: those dated strictly before
   * the calculated card. A date that cannot be read is dropped rather than
   * assumed to be earlier, so it can never seed a total — which also means
   * every item {@link newestValue} sees has a readable date.
   */
  private static cardsBefore<T extends FormulaHistoryItem>(
    history: T[],
    dprDate: Date | string | null
  ): T[] {
    const cutoffDay = getCalendarDay(dprDate);
    if (cutoffDay === null) {
      return [];
    }

    return (history ?? []).filter((item) => {
      const cardDay = getCalendarDay(item.dprDate);

      return cardDay !== null && cardDay < cutoffDay;
    });
  }

  /**
   * A cell value as text. Values are typed `string | null`, but a numeric grid
   * coerces its cells to real numbers before the engine sees them, and
   * `String.isNullOrWhiteSpace` throws on a number — one such cell would
   * otherwise blank the whole column, because the throw unwinds all the way
   * out of {@link calculateFormula}.
   */
  protected static asText(value: unknown): string | null {
    return value == null ? null : String(value);
  }

  /**
   * The newest value among matching history cells — the shared tail of both
   * subclasses' lookups, which differ only in how they match a cell to a row.
   *
   * A blank cell is skipped rather than allowed to shadow an older card: both
   * features store blanks routinely (a cell for every column on every row in
   * Table of Columns, a colour-only cell in Status Tracker), and what a
   * carry-forward needs is the most recent value the cell actually held.
   */
  protected static newestValue<T extends FormulaHistoryItem>(
    items: T[]
  ): string | null {
    let newestDay: string | null = null;
    let newestValue: string | null = null;

    for (const item of items ?? []) {
      const value = FormulaExpressionCalculationService.asText(item.value);

      if (String.isNullOrWhiteSpace(value)) {
        continue;
      }

      const cardDay = getCalendarDay(item.dprDate);

      if (cardDay !== null && (newestDay === null || cardDay > newestDay)) {
        newestDay = cardDay;
        newestValue = value;
      }
    }

    return newestValue;
  }

  /**
   * The row's carry-forward value for a column: its value on the previous DPR,
   * or null when there is none. A formula column has no configurable starting
   * value, so a caller starts the first card in a chain from "0".
   *
   * A blank must not reach the expression, where it would arrive as `()` and be
   * rejected, clearing the cell instead of leaving it empty. The
   * division-by-zero error is blank too: the API never sends it as a previous
   * value, so the offline walk must not carry it either.
   */
  private resolvePreviousValue(
    history: THistory[],
    columnId: number,
    rowId: number | string,
    anchorOptionId: number | null
  ): string | null {
    const previousValue = FormulaExpressionCalculationService.asText(
      this.getHistoryValue(history, columnId, rowId, anchorOptionId)
    );

    return String.isNullOrWhiteSpace(previousValue) ||
      isDivisionByZeroError(previousValue)
      ? null
      : previousValue;
  }

  /**
   * Replaces a single `$NNN` columnId with the given text. The replacement is
   * returned from a function so a `$` inside it is inserted literally instead
   * of being read as a back-reference.
   */
  private static replaceColumnId(
    expression: string,
    columnId: number,
    replacement: string
  ): string {
    const columnIdRegex = new RegExp(
      FormulaExpressionCalculationService.columnIdPattern(columnId),
      'g'
    );

    return expression.replace(columnIdRegex, () => replacement);
  }

  protected abstract getFormula(columnId: number): FormulaExpression | null;

  protected abstract getDetailItems(paramColumnId: number): FormulaCell[];

  /**
   * Every row the card has, in display order — not just the rows that happen to
   * hold a stored cell. A pure carry-over formula (`$self + 5`) has no
   * parameter cells to take a row list from, so an untouched row would
   * otherwise lose its carried-forward total for the day.
   */
  protected abstract getAllRowIds(): (number | string)[];

  /**
   * The row's anchor option id — the anchor cell's list-option value, which is
   * how a Table-of-Columns row is matched to the same logical row on an earlier
   * card. Null for Status Tracker, which matches by rowId.
   */
  protected abstract getAnchorOptionId(rowId: number | string): number | null;

  /**
   * Most recent historical value for (columnId, row) — TOC by anchor, ST by
   * rowId. `history` is already cut off at the calculated card's date, so
   * "most recent" means the latest earlier card.
   */
  protected abstract getHistoryValue(
    history: THistory[],
    columnId: number,
    rowId: number | string,
    anchorOptionId: number | null
  ): string | null;
}
